import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { setTokenGetter } from './lib/api';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { RequireRole } from './components/layout/RequireRole';
import { ToastContainer } from './components/common/Toast';
import { LocalDevBanner } from './components/common/LocalDevBanner';
import { HomePage, DashboardPage, FrameworkPage, SubmitPage, ProjectDetailPage, EditProjectPage, ProfilePage, GapsPage, HelpPage } from './pages';
import { FrameworkConfigPage } from './pages/admin/FrameworkConfig';
import { UserManagementPage } from './pages/admin/UserManagement';
import { CapabilitiesPage } from './pages/admin/Capabilities';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

function AppRoutes() {
  const { getAccessToken, user } = useAuth();

  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  return (
    <>
      <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/framework" element={<FrameworkPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/submit"
        element={
          <ProtectedRoute>
            <SubmitPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:id"
        element={
          <ProtectedRoute>
            <ProjectDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:id/edit"
        element={
          <ProtectedRoute>
            <EditProjectPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/framework"
        element={
          <RequireRole allow={['admin', 'framework-manager']}>
            <FrameworkConfigPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/users"
        element={
          <RequireRole allow={['admin']}>
            <UserManagementPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/capabilities"
        element={
          <RequireRole allow={['admin']}>
            <CapabilitiesPage />
          </RequireRole>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/gaps"
        element={
          <ProtectedRoute>
            <GapsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        }
      />
      </Routes>
      {user && <LocalDevBanner role={user.role} />}
    </>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <ToastContainer />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
